import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { appPageService } from '../services/appPageService';
import { Edit2, Trash2, Plus, CheckCircle, XCircle, FileText } from 'lucide-react';
// Remove react-quill for now due to dependency issues
// import ReactQuill from 'react-quill';
// import 'react-quill/dist/quill.snow.css';

const AppPages = () => {
    const [pages, setPages] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentPageId, setCurrentPageId] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        slug: '',
        content: '',
        is_active: true
    });

    const { user } = useSelector((state) => state.auth);
    const token = user?.token;

    useEffect(() => {
        fetchPages();
    }, []);

    const fetchPages = async () => {
        const data = await appPageService.getAllPages(token);
        setPages(data);
    };

    const handleTitleChange = (e) => {
        const title = e.target.value;
        const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
        setFormData({ ...formData, title, slug });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isEditing) {
            const res = await appPageService.updatePage(currentPageId, formData, token);
            if (res.success) {
                setShowModal(false);
                fetchPages();
            } else {
                alert(res.message);
            }
        } else {
            const res = await appPageService.createPage(formData, token);
            if (res.success) {
                setShowModal(false);
                fetchPages();
            } else {
                alert(res.message);
            }
        }
    };

    const handleEdit = (page) => {
        setIsEditing(true);
        setCurrentPageId(page._id);
        setFormData({
            title: page.title,
            slug: page.slug,
            content: page.content,
            is_active: page.is_active
        });
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this page?')) {
            const res = await appPageService.deletePage(id, token);
            if (res.success) {
                fetchPages();
            } else {
                alert(res.message);
            }
        }
    };

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800 flex items-center">
                    <FileText className="mr-2" /> App Content Pages
                </h1>
                <button
                    onClick={() => {
                        setIsEditing(false);
                        setFormData({ title: '', slug: '', content: '', is_active: true });
                        setShowModal(true);
                    }}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-blue-700 transition"
                >
                    <Plus size={20} className="mr-1" /> Create New Page
                </button>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <table className="w-full text-left">
                    <thead className="bg-gray-50 text-gray-600 uppercase text-xs font-semibold">
                        <tr>
                            <th className="px-6 py-4">Title</th>
                            <th className="px-6 py-4">Slug</th>
                            <th className="px-6 py-4">Status</th>
                            <th className="px-6 py-4">Last Updated</th>
                            <th className="px-6 py-4 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {pages.map((page) => (
                            <tr key={page._id} className="hover:bg-gray-50 transition">
                                <td className="px-6 py-4 font-medium text-gray-900">{page.title}</td>
                                <td className="px-6 py-4 text-gray-500">{page.slug}</td>
                                <td className="px-6 py-4">
                                    {page.is_active ? (
                                        <span className="flex items-center text-green-600 bg-green-50 px-2 py-1 rounded-full text-xs">
                                            <CheckCircle size={14} className="mr-1" /> Active
                                        </span>
                                    ) : (
                                        <span className="flex items-center text-red-600 bg-red-50 px-2 py-1 rounded-full text-xs">
                                            <XCircle size={14} className="mr-1" /> Inactive
                                        </span>
                                    )}
                                </td>
                                <td className="px-6 py-4 text-gray-500 text-sm">
                                    {new Date(page.updated_at).toLocaleDateString()}
                                </td>
                                <td className="px-6 py-4 text-right space-x-2">
                                    <button
                                        onClick={() => handleEdit(page)}
                                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                                        title="Edit"
                                    >
                                        <Edit2 size={18} />
                                    </button>
                                    <button
                                        onClick={() => handleDelete(page._id)}
                                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                                        title="Delete"
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {showModal && (
                <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                            <h2 className="text-xl font-bold text-gray-800">
                                {isEditing ? 'Edit App Page' : 'Create New App Page'}
                            </h2>
                            <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-200 rounded-full transition">
                                <XCircle size={24} className="text-gray-400" />
                            </button>
                        </div>
                        
                        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto flex-1 space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Title</label>
                                    <input
                                        required
                                        type="text"
                                        value={formData.title}
                                        onChange={handleTitleChange}
                                        placeholder="e.g., Privacy Policy"
                                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Slug (URL identifier)</label>
                                    <input
                                        required
                                        type="text"
                                        value={formData.slug}
                                        onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                                        placeholder="privacy-policy"
                                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-gray-50"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Content (HTML supported)</label>
                                <textarea
                                    required
                                    value={formData.content}
                                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                                    className="w-full h-[400px] px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                                    placeholder="Enter HTML content here..."
                                />
                            </div>

                            <div className="flex items-center space-x-3 pt-4">
                                <input
                                    type="checkbox"
                                    id="is_active"
                                    checked={formData.is_active}
                                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <label htmlFor="is_active" className="text-sm font-semibold text-gray-700 select-none">
                                    Active (Visible in App)
                                </label>
                            </div>
                        </form>

                        <div className="p-6 border-t border-gray-100 bg-gray-50/50 flex justify-end space-x-4">
                            <button
                                type="button"
                                onClick={() => setShowModal(false)}
                                className="px-6 py-2 border border-gray-300 rounded-xl text-gray-700 font-semibold hover:bg-gray-100 transition"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                onClick={handleSubmit}
                                className="px-8 py-2 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 shadow-md transition"
                            >
                                {isEditing ? 'Save Changes' : 'Create Page'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AppPages;
